import { UIUtils } from "@/shared/utils/UIUtils";
import { Heading } from "@/shared/components/ui";
import { cn } from "@/shared/utils";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag } from "@fortawesome/pro-regular-svg-icons";
import Strings from "@/shared/utils/Strings.constants";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";

interface PolicySummaryFooterProps {
    policiesCount: number;
    transactionFee: number;
    billingFrequency: "monthly" | "yearly";
    totalPrice: number;
    totalDiscount: number;
    isSingleCol?: boolean;
}

const PolicySummaryFooter = (props: PolicySummaryFooterProps) => {
    const { appState } = useAppLayerContext();
    const { partnerOffer } = appState;
    const { policiesCount, transactionFee, billingFrequency, totalPrice, totalDiscount, isSingleCol = false } = props;
    //Store the monthly fee to calculate (monthlyFee * 12) in annual mode.
    const [storedMonthlyFee, setStoredMonthlyFee] = useState(() => {
        return billingFrequency === "monthly" ? transactionFee : 0;
    });

    /*
      Edge case: if the user toggles to annual, changes ZIP (to no fee), and returns
      to billing step in annual mode, we won’t see the updated monthly fee until they
      toggle back to monthly.

      The latch has to survive the switch to annual (where `transactionFee` is no longer the monthly
      one), so it stays state; it is re-synced during render while on monthly rather than in an
      effect. The `!==` guard is what stops the update from looping.
  */
    if (billingFrequency === "monthly" && storedMonthlyFee !== transactionFee) {
        setStoredMonthlyFee(transactionFee);
    }

    const isAnnualBilling = billingFrequency === "yearly";

    const feeToDisplay = isAnnualBilling ? storedMonthlyFee * 12 : transactionFee;

    const formattedTotalPrice = UIUtils.formatNumber(totalPrice ?? 0, 2);

    return (
        <div className={cn("grid px-4 pb-4 text-content-primary", !isSingleCol && "sm:grid-cols-2 sm:gap-12")}>
            <div></div>
            <div className="flex flex-col gap-1">
                <div
                    className={cn("flex flex-row justify-between text-xs", {
                        "line-through": isAnnualBilling && feeToDisplay > 0
                    })}
                >
                    <p>Monthly Transaction Fee</p>
                    <p className="flex gap-2">
                        <b
                            className={cn({
                                "line-through": isAnnualBilling && feeToDisplay > 0,
                                "text-content-success": feeToDisplay === 0
                            })}
                            data-testid="monthly-transaction-fee"
                        >
                            {UIUtils.formatCurrency(feeToDisplay)}
                        </b>
                        {isAnnualBilling && feeToDisplay > 0 && <b className="text-content-success">$0.00</b>}
                    </p>
                </div>

                <div className="flex flex-row justify-between">
                    <Heading level="h4" className="text-base font-bold">
                        Grand Total
                    </Heading>
                    <div className={cn("text-base font-semibold", partnerOffer && "font-bold text-content-success")}>
                        <span data-testid="policy-summary-total-price" data-value={formattedTotalPrice}>
                            {`(${policiesCount} ${policiesCount === 1 ? "Pet" : "Pets"}) $${formattedTotalPrice}`}
                        </span>
                        /{isAnnualBilling ? "yr" : "mo"}
                    </div>
                </div>
                {partnerOffer && (
                    <div className="-mt-1 flex items-center justify-end gap-1 text-xs font-medium leading-5 text-content-success">
                        <FontAwesomeIcon icon={faTag} className="size-4" />
                        {Strings.DISCOUNT_SAVINGS} {UIUtils.formatCurrency(totalDiscount)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PolicySummaryFooter;
